import { useEffect, useState } from "react";
import { AxiosPropertyInstance } from "../common/utils/AxiosInstance";

/**
 * Wallet credit config (Super-Admin editable) — the joining/signup bonus and the
 * referral bonus, from the DB `credits` pricing singleton. Used so the referral
 * and signup surfaces DISPLAY the same amounts the backend actually grants.
 * Falls back to the historical 50 / 50 / 6 when the DB read is unavailable.
 * Fetched once per session and reused from the module cache.
 */
const DEFAULT_CREDITS_CONFIG = {
  joiningCredits: 50,
  referralCredits: 50,
  referralEarnLimit: 6,
};

let _cached = null;
let _inflight = null;

function loadCreditsConfig() {
  if (_cached != null) return Promise.resolve(_cached);
  if (!_inflight) {
    _inflight = AxiosPropertyInstance.get("/service-pricing/singleton/credits")
      .then((res) => {
        const cfg = res?.data || {};
        const num = (v, f) => {
          const n = Number(v);
          return Number.isFinite(n) && n >= 0 ? n : f;
        };
        _cached = {
          joiningCredits: num(
            cfg.joiningCredits,
            DEFAULT_CREDITS_CONFIG.joiningCredits
          ),
          referralCredits: num(
            cfg.referralCredits,
            DEFAULT_CREDITS_CONFIG.referralCredits
          ),
          referralEarnLimit: num(
            cfg.referralEarnLimit,
            DEFAULT_CREDITS_CONFIG.referralEarnLimit
          ),
        };
        return _cached;
      })
      .catch(() => {
        _cached = { ...DEFAULT_CREDITS_CONFIG };
        return _cached;
      });
  }
  return _inflight;
}

export function useCreditsConfig() {
  const [config, setConfig] = useState(_cached ?? DEFAULT_CREDITS_CONFIG);
  useEffect(() => {
    let alive = true;
    loadCreditsConfig().then((c) => {
      if (alive) setConfig(c);
    });
    return () => {
      alive = false;
    };
  }, []);
  return config;
}
